import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../lib/AuthProvider';
import { useScreen } from '../lib/useScreen';
import arbolCatalog from '../data/arbol.json';
import { calcularEstado } from '../lib/arbolData';

const font = "'DM Sans', sans-serif";
const serif = "'DM Serif Display', serif";
const C = { turquesa:'#5dc1b9', mango:'#FDBE02', coral:'#eb636b', morado:'#534AB7', dark:'#0a0a0a' };

const glassCard = {
  background: 'rgba(255,255,255,0.05)',
  backdropFilter: 'blur(30px)',
  WebkitBackdropFilter: 'blur(30px)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 24,
  padding: '24px',
  marginBottom: 20,
};

const sectionTitle = {
  fontSize: 11,
  color: 'rgba(255,255,255,0.4)',
  letterSpacing: 1.2,
  textTransform: 'uppercase',
  margin: '0 0 12px',
  fontWeight: 600,
};

const chipStyle = (color) => ({
  padding: '5px 12px',
  background: `${color}15`,
  border: `0.5px solid ${color}30`,
  borderRadius: 999,
  fontSize: 12,
  color: color,
  display: 'inline-block',
});

const divider = {
  height: 1,
  background: 'rgba(255,255,255,0.08)',
  border: 'none',
  margin: '0',
};

// Onsite projects — used for "upcoming" fallback when project isn't in DB yet
const ONSITE_FALLBACK = {
  'talabarteria-ga': { name: 'Talabarteria GA', subtitle: 'Cinturones y articulos en piel hechos a mano', location: 'Jocotitlan · Edomex', quote: 'Somos una familia con 20 años trabajando la piel. Cada pieza la hacemos nosotros.', desc: 'Don Gabriel y sus hijas perfeccionan el arte de la talabarteria en Jocotitlan. Cinturones, fundas y arneses cosidos uno por uno, sin maquinas industriales.', photo: '/cards/talabarteria-ga.jpg' },
  'rustico-el-once': { name: 'Rustico El Once', subtitle: 'Cabañas en el bosque para desconectar', location: 'Amealco · Queretaro', quote: 'Cuando los huespedes llegan, lo primero que dicen es: aqui se respira diferente.', desc: 'Cabañas de madera en medio del bosque queretano. Animales en libertad, hogueras al anochecer y cero señal de celular.', photo: '/cards/rustico-el-once.jpg' },
  'playwithus': { name: 'PlayWithUs', subtitle: 'Experiencias tematicas para fiestas y eventos', location: 'CDMX · Roma Norte', quote: 'Hacemos que los niños recuerden la fiesta por años. Esa es nuestra meta.', desc: 'Un equipo joven que diseña experiencias tematicas para fiestas infantiles en CDMX.', photo: '/cards/playwithus.jpg' },
  'bike-park': { name: 'Bike Park', subtitle: 'Pista de bicicletas en medio del bosque', location: 'Acambay · Edomex', quote: 'Queriamos un lugar donde la gente viniera a rodar y se olvidara de todo lo demas.', desc: 'Una pista de ciclismo de montaña enclavada en el bosque de Acambay.', photo: '/cards/bike-park.jpg' },
  'rac-pac': { name: 'Rac Pac', subtitle: 'Hojalateria y pintura automotriz', location: 'Acambay · Edomex', quote: 'Cada carro que entra sale como nuevo. Ese es nuestro compromiso con la gente.', desc: 'Taller de hojalateria y pintura en Acambay. Reparacion de carrocerias, enderezado, preparacion y pintura automotriz.', photo: '/cards/rac-pac.jpg' },
  'tierra-alta': { name: 'Tierra Alta', subtitle: 'Productos del campo y tradicion local', location: 'Atlacomulco · Edomex', quote: 'Lo que cosechamos aqui tiene historia. Cada producto viene de generaciones de trabajo.', desc: 'Un proyecto que nace de la tierra en Atlacomulco.', photo: '/cards/tierra-alta.jpg' },
  'gloria-psicologia': { name: 'Gloria', subtitle: 'Acompañamiento psicologico para emprendedores', location: 'Saltillo · Coahuila', quote: 'Emprender es mental. Si no cuidas lo que sientes, el negocio no avanza.', desc: 'Gloria acompaña a emprendedores desde la psicologia.', photo: '/cards/gloria.jpg' },
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export default function Proyecto() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user, membership } = useAuth();
  const { isMobile } = useScreen();
  const [perfil, setPerfil] = useState(null);
  const [pensamientos, setPensamientos] = useState([]);
  const [ciudad, setCiudad] = useState('');
  const [loading, setLoading] = useState(true);
  const [upcoming, setUpcoming] = useState(null);
  const [arbolProgreso, setArbolProgreso] = useState(null);
  const [arbolNegocio, setArbolNegocio] = useState(null);

  const isFree = !membership || membership === 'free_observer' || membership === 'tu_radar';
  const esMio = userId === user?.id;

  useEffect(() => {
    if (!userId) return;
    const cargar = async () => {
      let prof = null;

      if (UUID_RE.test(userId)) {
        // Lookup by UUID
        const { data } = await supabase.from('profiles').select('*').eq('id', userId).single();
        prof = data;
      } else {
        // Lookup by slug in business_name or display_name
        const { data } = await supabase
          .from('profiles')
          .select('*')
          .or(`slug.eq.${userId},business_name.ilike.%${userId.replace(/-/g, ' ')}%`)
          .limit(1)
          .maybeSingle();
        prof = data;
      }

      if (prof) {
        setPerfil(prof);

        // Resolve city name
        if (prof.city_id) {
          const { data: ci } = await supabase.from('cities').select('name').eq('id', prof.city_id).single();
          const { data: co } = await supabase.from('countries').select('name').eq('id', prof.country_id).single();
          setCiudad([ci?.name, co?.name].filter(Boolean).join(', '));
        } else if (prof.city) {
          setCiudad(prof.city);
        }

        // Fetch public thoughts
        const { data: pens } = await supabase
          .from('pensamientos')
          .select('*')
          .eq('user_id', prof.id)
          .eq('visibilidad', 'publico')
          .order('created_at', { ascending: false });
        setPensamientos(pens || []);

        // Fetch their arbol data
        const { data: otroNegocio } = await supabase
          .from('arbol_negocios').select('*').eq('user_id', prof.id).maybeSingle();

        if (otroNegocio) {
          setArbolNegocio(otroNegocio);
          const { data: otroProgreso } = await supabase
            .from('arbol_progreso').select('*').eq('negocio_id', otroNegocio.id);
          setArbolProgreso(otroProgreso || []);
        }
      } else if (ONSITE_FALLBACK[userId]) {
        // Project is in our hardcoded onsite list but not registered yet
        setUpcoming(ONSITE_FALLBACK[userId]);
      }

      setLoading(false);
    };
    cargar();
  }, [userId]);

  // Loading state
  if (loading) {
    return (
      <div style={{ minHeight:'100vh', background:C.dark, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', fontFamily:font }}>
        <div style={{ width:28, height:28, border:'3px solid rgba(255,255,255,0.08)', borderTopColor:'#5dc1b9', borderRadius:'50%', animation:'tdSpin 0.8s linear infinite', marginBottom:12 }} />
        <p style={{ color:'rgba(255,255,255,0.4)', fontSize:14 }}>Creando tu experiencia...</p>
        <style>{`@keyframes tdSpin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Upcoming / Próximamente state
  if (upcoming) {
    return (
      <div style={{ background:C.dark, minHeight:'100vh', fontFamily:font, color:'#fff' }}>
        <nav style={{ position:'sticky', top:0, zIndex:100, background:'rgba(10,10,10,0.85)', backdropFilter:'blur(12px)', WebkitBackdropFilter:'blur(12px)', borderBottom:'0.5px solid rgba(255,255,255,0.06)' }}>
          <div style={{ maxWidth:720, margin:'0 auto', padding:'0 1.5rem', height:56, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <button onClick={() => navigate(-1)} style={{ background:'none', border:'none', color:C.turquesa, fontSize:14, fontWeight:500, cursor:'pointer', fontFamily:font, padding:0 }}>← Volver</button>
            <div style={{ width:30, height:30, borderRadius:'50%', background:'linear-gradient(135deg, #534AB7, #1D9E75)', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <span style={{ color:'#fff', fontSize:9, fontWeight:600 }}>T&D</span>
            </div>
          </div>
        </nav>

        <div style={{ maxWidth:600, margin:'0 auto', padding:isMobile?'32px 16px':'48px 24px', textAlign:'center' }}>
          {/* Photo */}
          <div style={{ width:'100%', aspectRatio:'16/9', borderRadius:24, overflow:'hidden', marginBottom:28, position:'relative', background:'linear-gradient(135deg, rgba(93,193,185,0.15), rgba(83,74,183,0.1))' }}>
            <img
              src={upcoming.photo}
              alt={upcoming.name}
              onError={e => { e.target.style.display = 'none'; }}
              style={{ width:'100%', height:'100%', objectFit:'cover' }}
            />
          </div>

          {/* Info */}
          <h1 style={{ fontFamily:serif, fontSize:isMobile?28:36, fontWeight:400, margin:'0 0 6px' }}>{upcoming.name}</h1>
          <p style={{ fontSize:14, color:C.turquesa, fontWeight:500, margin:'0 0 8px' }}>{upcoming.subtitle}</p>
          <p style={{ fontSize:13, color:'rgba(255,255,255,0.5)', margin:'0 0 24px' }}>📍 {upcoming.location}</p>

          {/* Quote */}
          <div style={{ borderLeft:`2px solid ${C.mango}`, paddingLeft:16, textAlign:'left', marginBottom:24 }}>
            <p style={{ fontFamily:serif, fontSize:15, color:'rgba(255,255,255,0.75)', fontStyle:'italic', lineHeight:1.6, margin:0 }}>
              "{upcoming.quote}"
            </p>
          </div>

          <p style={{ fontSize:14, color:'rgba(255,255,255,0.6)', lineHeight:1.7, marginBottom:32, textAlign:'left' }}>{upcoming.desc}</p>

          {/* Upcoming banner */}
          <div style={{ ...glassCard, border:'1px solid rgba(93,193,185,0.2)', textAlign:'center', padding:isMobile?'28px 20px':'36px 28px' }}>
            <p style={{ fontSize:32, margin:'0 0 12px' }}>✨</p>
            <p style={{ fontFamily:serif, fontSize:isMobile?20:24, fontWeight:400, margin:'0 0 12px', color:'#fff' }}>
              {upcoming.name} esta por unirse a T&D
            </p>
            <p style={{ fontSize:14, color:'rgba(255,255,255,0.55)', lineHeight:1.7, margin:'0 0 24px' }}>
              Pronto vas a poder ver sus pensamientos y conectar directamente con ellos. Mientras tanto, conoce otros proyectos.
            </p>
            <button onClick={() => navigate('/sala')} style={{
              background:'linear-gradient(135deg, #5dc1b9, #534AB7)',
              color:'#fff', border:'none', borderRadius:50,
              padding:'13px 32px', fontSize:14, fontWeight:500,
              cursor:'pointer', fontFamily:font,
              boxShadow:'0 0 30px rgba(93,193,185,0.2)',
            }}>
              Ver otros proyectos en La Sala →
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Not found at all
  if (!perfil) {
    return (
      <div style={{ minHeight:'100vh', background:C.dark, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', fontFamily:font }}>
        <p style={{ fontSize:32, margin:'0 0 12px' }}>🔭</p>
        <p style={{ color:'rgba(255,255,255,0.5)', fontSize:15, margin:'0 0 20px' }}>Proyecto no encontrado</p>
        <button onClick={() => navigate('/sala')} style={{ background:'none', border:'1px solid rgba(255,255,255,0.15)', borderRadius:50, padding:'10px 24px', color:C.turquesa, fontSize:13, fontWeight:500, cursor:'pointer', fontFamily:font }}>
          Ir a La Sala →
        </button>
      </div>
    );
  }

  const nombre = perfil.business_name || perfil.full_name || 'Emprendedor';
  const skills = perfil.skill ? perfil.skill.split(', ') : [];
  const needs = perfil.pain_point ? perfil.pain_point.split(', ') : [];
  const stagePill = [perfil.business_type, perfil.business_stage].filter(Boolean).join(' · ');

  return (
    <div style={{ background:C.dark, minHeight:'100vh', fontFamily:font, color:'#fff' }}>
      {/* Nav */}
      <nav style={{ position:'sticky', top:0, zIndex:100, background:'rgba(10,10,10,0.85)', backdropFilter:'blur(12px)', WebkitBackdropFilter:'blur(12px)', borderBottom:'0.5px solid rgba(255,255,255,0.06)' }}>
        <div style={{ maxWidth:720, margin:'0 auto', padding:'0 1.5rem', height:56, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <button onClick={() => navigate(-1)} style={{ background:'none', border:'none', color:C.turquesa, fontSize:14, fontWeight:500, cursor:'pointer', fontFamily:font, padding:0 }}>
            ← Volver
          </button>
          <div style={{ width:30, height:30, borderRadius:'50%', background:'linear-gradient(135deg, #534AB7, #1D9E75)', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <span style={{ color:'#fff', fontSize:9, fontWeight:600 }}>T&D</span>
          </div>
        </div>
      </nav>

      <div style={{ maxWidth:720, margin:'0 auto', padding: isMobile ? '24px 16px' : '32px 24px' }}>
        {/* Hero: Avatar + Info */}
        <div style={{ display:'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'center' : 'flex-start', gap: isMobile ? 16 : 24, marginBottom:28, textAlign: isMobile ? 'center' : 'left' }}>
          {/* Avatar */}
          {perfil.avatar_url ? (
            <img src={perfil.avatar_url} alt="" style={{ width: isMobile ? 90 : 110, height: isMobile ? 90 : 110, borderRadius: '50%', objectFit:'cover', flexShrink:0, border:'2px solid rgba(255,255,255,0.1)' }} />
          ) : (
            <div style={{ width: isMobile ? 90 : 110, height: isMobile ? 90 : 110, borderRadius:'50%', background:'linear-gradient(135deg, #5dc1b9, #FDBE02)', display:'flex', alignItems:'center', justifyContent:'center', fontSize: isMobile ? 32 : 38, fontWeight:600, flexShrink:0 }}>
              {nombre[0]}
            </div>
          )}

          {/* Info */}
          <div>
            <h1 style={{ fontFamily:serif, fontSize: isMobile ? 24 : 30, fontWeight:400, margin:'0 0 6px' }}>{nombre}</h1>
            {perfil.full_name && perfil.business_name && perfil.full_name !== perfil.business_name && (
              <p style={{ margin:'0 0 6px', fontSize:14, color:'rgba(255,255,255,0.6)' }}>{perfil.full_name}</p>
            )}
            {stagePill && (
              <span style={{ display:'inline-block', padding:'4px 14px', borderRadius:999, background:'rgba(93,193,185,0.12)', border:'0.5px solid rgba(93,193,185,0.25)', fontSize:12, color:C.turquesa, marginBottom:8 }}>
                {stagePill}
              </span>
            )}
            {ciudad && <p style={{ margin:'4px 0 0', fontSize:13, color:'rgba(255,255,255,0.5)' }}>📍 {ciudad}</p>}
          </div>
        </div>

        <hr style={divider} />

        {/* SOBRE */}
        {(perfil.presentacion_descripcion || perfil.presentacion_quien || perfil.presentacion_que_hace) && (
          <div style={{ ...glassCard, marginTop:20 }}>
            <p style={sectionTitle}>Sobre</p>
            {perfil.presentacion_descripcion && (
              <p style={{ margin:'0 0 12px', fontSize:14, color:'rgba(255,255,255,0.85)', lineHeight:1.7 }}>{perfil.presentacion_descripcion}</p>
            )}
            {perfil.presentacion_quien && (
              <p style={{ margin:'0 0 8px', fontSize:14, color:'rgba(255,255,255,0.75)', lineHeight:1.6 }}>{perfil.presentacion_quien}</p>
            )}
            {perfil.presentacion_que_hace && (
              <p style={{ margin:0, fontSize:14, color:'rgba(255,255,255,0.75)', lineHeight:1.6 }}>{perfil.presentacion_que_hace}</p>
            )}
          </div>
        )}

        {/* QUE BUSCA */}
        {perfil.presentacion_que_busca && (
          <>
            <hr style={divider} />
            <div style={{ ...glassCard, marginTop:20 }}>
              <p style={sectionTitle}>Que busca</p>
              <p style={{ margin:0, fontSize:14, color:'rgba(255,255,255,0.85)', lineHeight:1.7 }}>{perfil.presentacion_que_busca}</p>
            </div>
          </>
        )}

        {/* SKILLS Y NECESIDADES */}
        {(skills.length > 0 || needs.length > 0) && (
          <>
            <hr style={divider} />
            <div style={{ ...glassCard, marginTop:20 }}>
              <p style={sectionTitle}>Skills y necesidades</p>
              {skills.length > 0 && (
                <div style={{ marginBottom: needs.length > 0 ? 16 : 0 }}>
                  <p style={{ fontSize:12, color:'rgba(255,255,255,0.5)', margin:'0 0 8px' }}>Soy bueno en</p>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                    {skills.map(s => <span key={s} style={chipStyle(C.turquesa)}>{s}</span>)}
                  </div>
                </div>
              )}
              {needs.length > 0 && (
                <div>
                  <p style={{ fontSize:12, color:'rgba(255,255,255,0.5)', margin:'0 0 8px' }}>Necesito ayuda con</p>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                    {needs.map(n => <span key={n} style={chipStyle(C.mango)}>{n}</span>)}
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* PENSAMIENTOS ATOMICOS */}
        {pensamientos.length > 0 && (
          <>
            <hr style={divider} />
            <div style={{ ...glassCard, marginTop:20 }}>
              <p style={sectionTitle}>Pensamientos atomicos</p>
              {pensamientos.map(p => (
                <div key={p.id} style={{ padding:'10px 14px', background:'rgba(255,255,255,0.04)', border:'0.5px solid rgba(255,255,255,0.08)', borderRadius:12, marginBottom:8 }}>
                  <p style={{ margin:0, fontSize:14, fontWeight:500 }}>{p.titulo}</p>
                  <p style={{ margin:'3px 0 0', fontSize:11, color:'rgba(255,255,255,0.4)' }}>{new Date(p.created_at).toLocaleDateString('es')}</p>
                </div>
              ))}
            </div>
          </>
        )}

        {/* ARBOL DE MISIONES */}
        {arbolNegocio && arbolProgreso && (() => {
          const estado = calcularEstado(arbolCatalog.mundos, arbolCatalog.misiones, arbolProgreso);
          const progresoSet = new Set(arbolProgreso.map(p => p.mision_id));
          const totalMain = arbolCatalog.misiones.filter(m => !m.side_quest).length;
          const doneMain = arbolCatalog.misiones.filter(m => !m.side_quest && progresoSet.has(m.id)).length;
          const pct = totalMain > 0 ? Math.round((doneMain / totalMain) * 100) : 0;
          const mundoActivoObj = arbolCatalog.mundos.find(w => w.id === estado.mundoActivo);
          const RAMAS = ['Marca','Producto','Cliente','Administración','Legal','Equipo','Innovación','Fondeo'];
          const ramaColors = { Marca:'#5dc1b9', Producto:'#FDBE02', Cliente:'#eb636b', 'Administración':'#8B5CF6', Legal:'#3B82F6', Equipo:'#F97316', 'Innovación':'#10B981', Fondeo:'#EC4899' };
          const ramaMax = {};
          arbolCatalog.misiones.forEach(m => { ramaMax[m.rama] = (ramaMax[m.rama] || 0) + 1; });

          return (
            <>
              <hr style={divider} />
              <div style={{ ...glassCard, marginTop: 20, background: 'rgba(93,193,185,0.04)', border: '1px solid rgba(93,193,185,0.15)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                  <span style={{ fontSize: 24 }}>🌳</span>
                  <div>
                    <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#F3F4F6' }}>Árbol de Misiones</p>
                    <p style={{ margin: '2px 0 0', fontSize: 12, color: '#8C97B0' }}>
                      Mundo actual: {String(estado.mundoActivo).padStart(2, '0')} · {mundoActivoObj?.nombre || ''}
                    </p>
                  </div>
                </div>

                {/* Stats row */}
                <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12, color: '#8C97B0' }}>Nivel <strong style={{ color: '#5dc1b9' }}>{String(estado.nivel).padStart(2, '0')}</strong></span>
                  <span style={{ fontSize: 12, color: '#8C97B0' }}>{estado.pines} pines</span>
                  <span style={{ fontSize: 12, color: '#8C97B0' }}>{estado.insignias.length} insignias</span>
                </div>

                {/* Progress bar */}
                <div style={{ marginBottom: 20 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ fontSize: 11, color: '#6B7084' }}>Progreso general</span>
                    <span style={{ fontSize: 11, color: '#5dc1b9', fontWeight: 700 }}>{pct}%</span>
                  </div>
                  <div style={{ height: 8, background: '#1A2236', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: 'linear-gradient(90deg, #5dc1b9, #FDBE02)', borderRadius: 4, transition: 'width 0.5s ease' }} />
                  </div>
                </div>

                {/* World list */}
                <p style={{ ...sectionTitle, marginBottom: 10 }}>Mundos</p>
                {arbolCatalog.mundos.map(w => {
                  const mainMissions = arbolCatalog.misiones.filter(m => m.mundo_id === w.id && !m.side_quest);
                  const done = mainMissions.filter(m => progresoSet.has(m.id)).length;
                  const total = mainMissions.length;
                  const vencido = estado.mundoVencidoMap[w.id];

                  return (
                    <div key={w.id} style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '12px 16px', marginBottom: 8,
                      background: vencido ? 'rgba(93,193,185,.08)' : 'rgba(255,255,255,0.03)',
                      border: `1px solid ${vencido ? 'rgba(93,193,185,.3)' : 'rgba(255,255,255,0.06)'}`,
                      borderRadius: 12,
                    }}>
                      <div style={{
                        width: 40, height: 40, borderRadius: '50%',
                        background: vencido ? 'rgba(93,193,185,.15)' : 'rgba(255,255,255,0.04)',
                        border: `2px solid ${vencido ? '#5dc1b9' : 'rgba(255,255,255,0.1)'}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 14, fontWeight: 700,
                        color: vencido ? '#5dc1b9' : '#6B7084',
                        flexShrink: 0,
                      }}>
                        {vencido ? '✓' : String(w.id).padStart(2, '0')}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: vencido ? '#F3F4F6' : '#8C97B0' }}>
                          {w.nombre}
                        </div>
                        <div style={{ fontSize: 11, color: '#6B7084' }}>
                          {done}/{total} misiones{vencido && w.insignia ? ` · 🏅 ${w.insignia}` : ''}
                        </div>
                      </div>
                      <div style={{ width: 50, textAlign: 'right', fontSize: 13, fontWeight: 700, color: vencido ? '#5dc1b9' : '#6B7084', flexShrink: 0 }}>
                        {vencido ? '✓' : `${done}/${total}`}
                      </div>
                    </div>
                  );
                })}

                {/* Ramas stats */}
                <p style={{ ...sectionTitle, marginTop: 20, marginBottom: 10 }}>Ramas</p>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 8 }}>
                  {RAMAS.map(rama => {
                    const val = estado.stats[rama] || 0;
                    const max = ramaMax[rama] || 1;
                    const ramaPct = Math.round((val / max) * 100);
                    const color = ramaColors[rama] || '#5dc1b9';
                    return (
                      <div key={rama} style={{ padding: '8px 12px', background: 'rgba(255,255,255,0.03)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.06)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <span style={{ fontSize: 11, color: '#8C97B0' }}>{rama}</span>
                          <span style={{ fontSize: 11, color, fontWeight: 700 }}>{val}/{max}</span>
                        </div>
                        <div style={{ height: 4, background: 'rgba(255,255,255,0.06)', borderRadius: 2, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${ramaPct}%`, background: color, borderRadius: 2 }} />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Insignias */}
                {estado.insignias.length > 0 && (
                  <>
                    <p style={{ ...sectionTitle, marginTop: 20, marginBottom: 10 }}>Insignias</p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                      {estado.insignias.map((ins, i) => (
                        <div key={i} style={{
                          width: 72, textAlign: 'center',
                        }}>
                          <div style={{
                            width: 48, height: 48, margin: '0 auto 4px',
                            background: 'rgba(253,190,2,0.12)',
                            border: '2px solid rgba(253,190,2,0.4)',
                            borderRadius: 8,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 20,
                            transform: 'rotate(45deg)',
                          }}>
                            <span style={{ transform: 'rotate(-45deg)' }}>🏅</span>
                          </div>
                          <span style={{ fontSize: 9, color: '#FDBE02', fontWeight: 600, lineHeight: 1.2, display: 'block' }}>{ins}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </>
          );
        })()}

        {/* ACTION BUTTON */}
        {!esMio && (
          <>
            <hr style={divider} />
            <div style={{ marginTop:20, marginBottom:40 }}>
              {isFree ? (
                <button style={{ width:'100%', padding:'14px 0', background:'none', border:'1px solid rgba(253,190,2,0.3)', borderRadius:14, color:C.mango, fontSize:14, fontWeight:500, cursor:'pointer', fontFamily:font }}>
                  🔒 Conectar · Spark $139/mes
                </button>
              ) : (
                <button style={{ width:'100%', padding:'14px 0', background:C.turquesa, border:'none', borderRadius:14, color:'#fff', fontSize:14, fontWeight:500, cursor:'pointer', fontFamily:font }}>
                  💬 Conectar
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
